import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { useUserPreferences } from '../hooks/useUserPreferences';
import WalletRequired from '../components/WalletRequired';
import ConfirmDialog from '../components/ConfirmDialog';

const Settings = () => {
  const { publicKey } = useWallet();
  const {
    preferences,
    isLoading: preferencesLoading,
    isSaving,
    error: preferencesError,
    lastSyncedAt,
    updatePreferences,
    resetPreferences
  } = useUserPreferences();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  // Helper function to show notifications
  const showNotification = (message, type = 'error') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };
  
  // Local state for form (synced with preferences)
  const [localPrefs, setLocalPrefs] = useState({});

  // Sync local preferences with loaded preferences
  useEffect(() => {
    if (preferences) {
      setLocalPrefs(preferences);
      setHasUnsavedChanges(false);
    }
  }, [preferences]);

  const handlePreferenceChange = (key, value) => {
    setLocalPrefs(prev => ({
      ...prev,
      [key]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Helper function to safely toggle a boolean preference
  const togglePreference = (key) => {
    const currentValue = localPrefs[key];
    const newValue = !Boolean(currentValue);
    handlePreferenceChange(key, newValue);
  };

  const handleSaveChanges = async () => {
    try {
      await updatePreferences(localPrefs);
      
      setShowSuccessMessage(true);
      setHasUnsavedChanges(false);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Failed to save settings. Please try again.');
    }
  };

  const handleResetSettings = () => {
    console.log('handleResetSettings called - opening custom dialog');
    setShowResetDialog(true);
  };

  const confirmResetSettings = async () => {
    try {
      await resetPreferences();
      setShowSuccessMessage(true);
      setHasUnsavedChanges(false);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error resetting settings:', error);
      showNotification('Failed to reset settings. Please try again.');
    }
  };

  const ToggleSwitch = ({ enabled, onChange, label, description }) => {
    // Ensure enabled is treated as a boolean
    const isEnabled = Boolean(enabled);
    
    return (
      <div className="flex items-center justify-between p-4 rounded-xl border border-[#2a2a2a] bg-[#0a0a0a] hover:border-white transition-all duration-200">
        <div>
          <h4 className="font-medium text-white">{label}</h4>
          <p className="text-sm text-gray-400">{description}</p>
        </div>
        <button
          type="button"
          onClick={onChange}
          aria-label={`Toggle ${label}`}
          aria-checked={isEnabled}
          role="switch"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 ${
            isEnabled ? 'bg-blue-500' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
              isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    );
  };

  if (preferencesLoading) {
    return (
      <WalletRequired
        message="You need to connect your wallet to access settings."
        action="access settings"
      >
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading your settings...</p>
          </div>
        </div>
      </WalletRequired>
    );
  }

  return (
    <WalletRequired
      message="You need to connect your wallet to access settings and manage your account preferences."
      action="access settings"
    >
      <div className="min-h-screen bg-black">
        {/* Header Section */}
        <div className="max-w-full mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
              <p className="text-gray-400">Manage your account preferences</p>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={handleResetSettings}
                className="px-4 py-3 rounded-xl font-medium text-white transition-all duration-200 hover:scale-105 bg-[#2a2a2a] hover:bg-[#3a3a3a] border border-[#2a2a2a] hover:border-white shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Reset
              </button>
              
              <button 
                onClick={handleSaveChanges}
                disabled={isSaving || !hasUnsavedChanges}
                className="px-6 py-3 rounded-xl font-medium text-white transition-all duration-200 hover:scale-105 bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-full mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div 
                className="rounded-2xl border border-[#2a2a2a] p-4 shadow-xl sticky top-4"
                style={{ backgroundColor: '#111111' }}
              >
                <div className="mb-4">
                  <h3 className="text-white font-semibold">Settings Menu</h3>
                  <p className="text-gray-400 text-sm">Configure your preferences</p>
                </div>
                <nav className="space-y-2">
                  <div className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white bg-[#2a2a2a] shadow-lg">
                    <Bell size={16} />
                    Notifications
                  </div>
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div 
                className="rounded-2xl border border-[#2a2a2a] p-6 shadow-xl"
                style={{ backgroundColor: '#111111' }}
              >
                {/* NOTIFICATIONS */}
                {localPrefs && (
                  <div className="space-y-6">
                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Bell size={24} />
                        Notification Preferences
                      </h2>
                      <p className="text-gray-400 text-sm mt-1">Control how and when you receive notifications</p>
                    </div>
                    
                    {/* Delivery Methods */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-white mb-4">Delivery Methods</h3>
                      <div className="space-y-4">
                        <ToggleSwitch
                          enabled={localPrefs.push_enabled}
                          onChange={() => togglePreference('push_enabled')}
                          label="Push Notifications"
                          description="Receive browser push notifications"
                        />
                      </div>
                    </div>

                    {/* Trading & Marketplace */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-white mb-4">Trading & Marketplace</h3>
                      <div className="space-y-4">
                        <ToggleSwitch
                          enabled={localPrefs.notify_offers}
                          onChange={() => togglePreference('notify_offers')}
                          label="New Offers"
                          description="When someone makes an offer on your piece"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_offer_accepted}
                          onChange={() => togglePreference('notify_offer_accepted')}
                          label="Offer Accepted"
                          description="When your offer is accepted"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_offer_declined}
                          onChange={() => togglePreference('notify_offer_declined')}
                          label="Offer Declined"
                          description="When your offer is declined"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_sales}
                          onChange={() => togglePreference('notify_sales')}
                          label="Piece Sales"
                          description="When one of your pieces sells"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_purchases}
                          onChange={() => togglePreference('notify_purchases')}
                          label="Purchase Confirmations"
                          description="Confirmation when you buy a piece"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_listings}
                          onChange={() => togglePreference('notify_listings')}
                          label="Listing Updates"
                          description="When your piece is listed or delisted"
                        />
                      </div>
                    </div>

                    {/* P2P Trading */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-white mb-4">P2P Trading</h3>
                      <div className="space-y-4">
                        <ToggleSwitch
                          enabled={localPrefs.notify_trades}
                          onChange={() => togglePreference('notify_trades')}
                          label="Trade Proposals"
                          description="When someone proposes a trade"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_trade_accepted}
                          onChange={() => togglePreference('notify_trade_accepted')}
                          label="Trade Accepted"
                          description="When your trade proposal is accepted"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_trade_declined}
                          onChange={() => togglePreference('notify_trade_declined')}
                          label="Trade Declined"
                          description="When your trade proposal is declined"
                        />
                      </div>
                    </div>

                    {/* Social & Discovery */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-white mb-4">Social & Discovery</h3>
                      <div className="space-y-4">
                        <ToggleSwitch
                          enabled={localPrefs.notify_favorites}
                          onChange={() => togglePreference('notify_favorites')}
                          label="Favorites"
                          description="When someone favorites your piece or capsule"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_capsule_drops}
                          onChange={() => togglePreference('notify_capsule_drops')}
                          label="Capsule Drops"
                          description="New drops from creators you follow"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_collection_featured}
                          onChange={() => togglePreference('notify_collection_featured')}
                          label="Featured Collections"
                          description="When your collection is trending or featured"
                        />
                      </div>
                    </div>

                    {/* Price Alerts */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-white mb-4">Price Alerts</h3>
                      <div className="space-y-4">
                        <ToggleSwitch
                          enabled={localPrefs.notify_price_alerts}
                          onChange={() => togglePreference('notify_price_alerts')}
                          label="SOL Price Alerts"
                          description="Significant SOL price changes"
                        />
                      </div>
                    </div>

                    {/* System & Other */}
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">System & Other</h3>
                      <div className="space-y-4">
                        <ToggleSwitch
                          enabled={localPrefs.notify_verification}
                          onChange={() => togglePreference('notify_verification')}
                          label="Account Updates"
                          description="Updates about your account status and achievements"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_system}
                          onChange={() => togglePreference('notify_system')}
                          label="System Announcements"
                          description="Important platform updates and maintenance"
                        />
                        
                        <ToggleSwitch
                          enabled={localPrefs.notify_marketing}
                          onChange={() => togglePreference('notify_marketing')}
                          label="Marketing & Promotions"
                          description="Promotional content and special offers"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Success Notification */}
        {showSuccessMessage && (
          <div className="fixed top-4 right-4 z-[70] animate-in slide-in-from-top-2 duration-300">
            <div className="bg-green-600 border border-green-500/30 rounded-xl p-4 shadow-lg max-w-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle size={14} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">Settings saved successfully!</p>
                  <p className="text-green-100 text-xs mt-1">Your preferences are synced across all devices</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {preferencesError && (
          <div className="fixed bottom-4 right-4 z-[70]">
            <div className="bg-red-600 border border-red-500/30 rounded-xl p-4 shadow-lg max-w-sm">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-white flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">Error loading settings</p>
                  <p className="text-red-100 text-xs mt-1">{preferencesError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showResetDialog}
          onClose={() => setShowResetDialog(false)}
          onConfirm={confirmResetSettings}
          title="Reset All Settings"
          message="Are you sure you want to reset all settings to defaults? This cannot be undone."
          confirmText="Reset"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
          showWarningIcon={true}
        />

      </div>
    </WalletRequired>
  );
};

export default Settings;
